import type { MockedResponse } from '@apollo/client/testing'

import { GetPermissionResolutionDocument } from '@acre/graphql'
import type { PermissionScopeType } from '@acre/graphql/constants'
import { fixture } from '@acre/test'
import { GATE_TO_PERMISSION } from '@acre/utils'

type PermissionScopeInputOverride = { id?: InputMaybe<string>; type?: InputMaybe<PermissionScopeType> }

export interface PermissionFixtureBuilderConfigContext {
  scope?: PermissionScopeInputOverride
  assignee?: PermissionResolutionAssigneeInput
}

export interface PermissionFixtureBuilderConfig {
  gate: PermissionGateName
  scopes?: PermissionScopeInputOverride[]
  assignees?: PermissionResolutionAssigneeInput[]
  contexts?: PermissionFixtureBuilderConfigContext[]
}

export interface PermissionFixtureBuilderArgs {
  permissions: PermissionFixtureBuilderConfig[]
  disable?: boolean
  exceptions?: PermissionGateName[]
}

const permissionFixtureBuilder = (props: PermissionFixtureBuilderArgs) => {
  const { permissions, disable = false, exceptions = [] } = props

  const fixtures = permissions.reduce((acc, { gate, scopes, assignees, contexts }) => {
    if (exceptions.includes(gate)) {
      return acc
    }

    if ((assignees || scopes) && contexts) {
      console.error('[permissionFixtureBuilder] Do not pass scopes or assignees to this config when using contexts')
      return acc
    }

    if (scopes) {
      acc.push(
        ...scopes.map((scope) => ({
          request: {
            query: GetPermissionResolutionDocument,
            variables: { input: { permission_name: GATE_TO_PERMISSION[gate], scope } },
          },
          result: {
            data: {
              permissionResolution: fixture({
                name: 'resolvedPermissionResolution',
                overrides: {
                  has_permission: disable ? false : true,
                  permission_name: GATE_TO_PERMISSION[gate],
                  gate,
                  scope,
                },
              }),
            },
          },
        })),
      )
    }

    if (assignees) {
      acc.push(
        ...assignees.map((assignee) => ({
          request: {
            query: GetPermissionResolutionDocument,
            variables: { input: { permission_name: GATE_TO_PERMISSION[gate], assignee } },
          },
          result: {
            data: {
              permissionResolution: fixture({
                name: 'resolvedPermissionResolution',
                overrides: {
                  has_permission: disable ? false : true,
                  permission_name: GATE_TO_PERMISSION[gate],
                  gate,
                  assignee,
                },
              }),
            },
          },
        })),
      )
    }

    if (contexts) {
      acc.push(
        ...contexts.map(({ scope, assignee }) => ({
          request: {
            query: GetPermissionResolutionDocument,
            variables: { input: { permission_name: GATE_TO_PERMISSION[gate], scope, assignee } },
          },
          result: {
            data: {
              permissionResolution: fixture({
                name: 'resolvedPermissionResolution',
                overrides: {
                  has_permission: disable ? false : true,
                  permission_name: GATE_TO_PERMISSION[gate],
                  gate,
                  scope,
                  assignee,
                },
              }),
            },
          },
        })),
      )
    }

    return acc
  }, [] as MockedResponse[])

  return fixtures
}

export default permissionFixtureBuilder
